import { Button, Card, Confirm, Heading, Inline, Paragraph, Stack } from 'canoe';
import {
  Action,
  ActionFetch,
  ENDPOINTS,
  connectWithHammock,
  createCacheKey,
  fetchResource,
  handleErrorResponse,
  useCreatePayload,
} from 'hammock';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { ContentMetadata } from '@candu-ai/content/types';
import { useNotification } from '@candu-ai/users/notification';

import { ChangeSlug } from './components/ChangeSlug';
import Import from './components/Import';
import { Information } from './components/Information';
import { MoveSection } from './components/MoveSection';

export const validationSchema = Yup.object().shape({
  name: Yup.string().required('Please enter a name for your content'),
  description: Yup.string(),
});

interface ContentSettingsProps {
  contentMetadata: ContentMetadata;
  contentId: string;
  onContentCloned?: (id: string) => void;
  onContentArchived?: VoidFunction;
}

export const ContentSettingsImpl = ({
  contentMetadata,
  contentId,
  onContentCloned,
  onContentArchived,
}: ContentSettingsProps) => {
  const queryClient = useQueryClient();
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showChangeSlugModal, setShowChangeSlugModal] = useState(false);
  const notification = useNotification();
  const { action, get } = useCreatePayload(ENDPOINTS.contentMetadata);

  const contentCacheKey = createCacheKey(get({ id: contentId }));

  const { mutateAsync: archiveContent, isLoading: isArchiving } = useMutation(
    () => {
      const payload = action(({
        query: { action: 'archive' },
        body: {
          contentIds: [contentId],
          isArchived: true,
        },
      } as unknown) as Action);
      return fetchResource(payload).catch((e) => handleErrorResponse(e, payload, notification));
    },
    {
      onSuccess: () => {
        notification.addNotification({
          message: 'Content Archived',
        });
        onContentArchived?.();
      },
    },
  );

  const { mutateAsync: cloneContent, isLoading: isCloning } = useMutation(
    () => {
      const payload = action({ query: { contentId }, name: 'clone' });
      return fetchResource<ContentMetadata>(payload).catch((e) =>
        handleErrorResponse(e, payload, notification),
      );
    },
    {
      onSuccess: (clonedContent) => {
        notification.addNotification({
          message: `Content Successfully Cloned`,
        });
        onContentCloned?.(clonedContent.id);
      },
    },
  );

  return (
    <Stack>
      <Heading size="S">Content Information</Heading>
      <Paragraph maxWidth="none">General information about your Content</Paragraph>
      <Stack spaceAfter="normal">
        <Card>
          <Heading size="XS">Content Details</Heading>
          <Paragraph maxWidth="none">
            Use the following optional text boxes to rename your content and add a description to
            differentiate it from other content.
          </Paragraph>
          <Information
            contentMetadata={contentMetadata}
            onContentUpdated={() => {
              notification.addNotification({ message: 'Successfuly updated' });
              queryClient.invalidateQueries(contentCacheKey);
            }}
            onContentUpdateError={(error: any, actionPayload: ActionFetch) => {
              handleErrorResponse(error, actionPayload, notification);
            }}
          />
        </Card>
      </Stack>

      {/* Manage Content */}
      <Heading size="S">Manage Content</Heading>
      <Paragraph maxWidth="none">Import, clone and manage your content organization.</Paragraph>
      <Stack spaceAfter="normal">
        <Card>
          <Heading size="XS">Import Content</Heading>
          <Paragraph maxWidth="none">
            Import documents attached to other pieces of content, either in this workspace or
            another.
          </Paragraph>
          <Inline justify="flex-end">
            <Button onClick={() => setShowImportModal(true)} variant="secondary">
              Import Content
            </Button>
          </Inline>
        </Card>

        <Card>
          <Heading size="XS">Clone Content</Heading>
          <Paragraph maxWidth="none">
            Create an exact copy of this content. Analytics and other connected information will not
            be copied.
          </Paragraph>

          <Inline justify="flex-end">
            <Button onClick={() => cloneContent()} loading={isCloning} variant="secondary">
              Clone Content
            </Button>
          </Inline>
        </Card>

        <Card>
          <Heading size="XS">Move To Section</Heading>
          <Paragraph maxWidth="none">Organize your content in sections</Paragraph>
          <MoveSection
            onContentMoved={() => {
              notification.addNotification({ message: 'Successfuly moved content' });
              queryClient.invalidateQueries(contentCacheKey);
            }}
            onContentMovedError={(error: any, actionPayload: ActionFetch) => {
              handleErrorResponse(error, actionPayload, notification);
            }}
            contentMetadata={contentMetadata}
          />
        </Card>
      </Stack>

      {/* Danger Zone */}
      <Heading size="S">Danger Zone</Heading>
      <Paragraph maxWidth="none">Proceed with caution.</Paragraph>
      <Stack spaceAfter="normal">
        <Card>
          <Heading size="XS">Archive Content</Heading>
          <Paragraph maxWidth="none">
            Archiving content will make it inaccessible to users.
          </Paragraph>
          <Inline justify="flex-end">
            <Button onClick={() => setShowArchiveConfirm(true)} variant="danger">
              Archive Content
            </Button>
          </Inline>
        </Card>

        <Card>
          <Heading size="XS">Change Slug</Heading>
          <Paragraph maxWidth="none">
            Careful! The content slug is used to identify your content in code. If you change a
            content slug without updating it customers may not be able to access its content.
          </Paragraph>
          <Inline justify="flex-end">
            <Button onClick={() => setShowChangeSlugModal(true)} variant="danger">
              Change Slug
            </Button>
          </Inline>
        </Card>
      </Stack>

      {showImportModal && (
        <Import
          contentMetadata={contentMetadata}
          contentId={contentId}
          onClose={() => setShowImportModal(false)}
        />
      )}
      {showArchiveConfirm && (
        <Confirm
          severity="Danger"
          title="Archive Content?"
          message="Your Content is currently Published and Live. Your customers might be seeing the current Content. Archiving it now will cause them to stop seeing the Content."
          confirmButton={{ children: `Archive Content`, loading: isArchiving }}
          withCheckbox
          onClose={() => setShowArchiveConfirm(false)}
          onConfirm={archiveContent}
        />
      )}

      {showChangeSlugModal && (
        <ChangeSlug
          onClose={() => setShowChangeSlugModal(false)}
          contentMetadata={contentMetadata}
          onContentUpdated={() => {
            notification.addNotification({ message: 'Slug Successfuly Updated' });
            queryClient.invalidateQueries(contentCacheKey);
            setShowChangeSlugModal(false);
          }}
          onContentUpdateError={(error, actionPayload) => {
            handleErrorResponse(error, actionPayload, notification);
          }}
        />
      )}
    </Stack>
  );
};

export const ContentSettings = connectWithHammock(({ contentId }) => ({
  endpoint: ENDPOINTS.contentMetadata,
  id: contentId,
  resourceName: 'contentMetadata',
}))(ContentSettingsImpl);
